if (!customElements.get("media-gallery")) {
  customElements.define(
    "media-gallery",
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.selectors = {
          mediaGallery: "[id^=Media-Gallery]",
          liveRegion: '[id^="GalleryStatus"]',
          viewer: '[id^="GalleryViewer"]',
          thumbnailsWrapper: ".f-product__media-thumbnails-wrapper",
          thumbnails: '[id^="GalleryThumbnails"]',
          thumbnailItems: ['[id^="GalleryThumbnailItem"]'],
          videos: [".f-video__embed"],
          models: ["product-model"],
          medias: [".f-product__media"],
          xrButton: "[data-first-xr-button]",
          sliderCounter: ".flickity-counter--current",
          toggleZoom: [".js-photoswipe--zoom"],
        };

        let touchingCarousel = false;
        let startXCoordinate;

        document.body.addEventListener(
          "touchstart",
          (e) => {
            if (e.target.closest(".flickity-slider")) {
              touchingCarousel = true;
            } else {
              touchingCarousel = false;
              return;
            }
            startXCoordinate = e.touches[0].pageX;
          },
          { passive: true }
        );

        document.body.addEventListener(
          "touchmove",
          (e) => {
            if (touchingCarousel && e.cancelable) {
              if (Math.abs(e.touches[0].pageX - startXCoordinate) > 10) {
                e.preventDefault();
              }
            }
          },
          { passive: false }
        );
      }

      connectedCallback() {
        this.init();
        this.onVariantChangeUnsubscriber = window.FoxThemeEvents.subscribe(
          PUB_SUB_EVENTS.variantChange,
          (event) => {
            if (event.data.sectionId === this.sectionId) {
              this.currentVariant = event.data.variant;
              this.filterMediaBySwatch(false);
            }
          }
        );
        this.onOptionValueChangeUnsubscriber = window.FoxThemeEvents.subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          (event) => {
            if (this.section && event.data && event.data.target && this.section.contains(event.data.target)) {
              this.filterMediaBySwatch(false);
            }
          }
        );
      }

      disconnectedCallback() {
        clearInterval(this.check);
        if (this.onVariantChangeUnsubscriber) {
          this.onVariantChangeUnsubscriber();
        }
        if (this.onOptionValueChangeUnsubscriber) {
          this.onOptionValueChangeUnsubscriber();
        }
      }

      getSelectedVariant() {
        const selectedVariant = this.querySelector(
          "[data-selected-variant]"
        )?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      init() {
        this.domNodes = queryDomNodes(this.selectors, this);
        this.mediaLayout = this.domNodes.mediaGallery.dataset.mediaLayout;
        this.onlyImage = this.dataset.onlyImage === "true";
        this.sectionId = this.dataset.sectionId;
        this.section = this.closest("product-info");
        this.enableZoom = this.dataset.enableZoom === "true";
        this.enableVariantGroupImages =
          this.dataset.enableVariantGroupImages === "true";
        this.showFeaturedMedia = this.dataset.showFeaturedMedia === "true";
        this.variantPickers = this.section.querySelector(
          "[data-variant-picker]"
        );
        this.disableDefaultVariant =
          this.variantPickers &&
          this.variantPickers.dataset.disableDefaultVariant === "true";
        this.variantData = this.getVariantData();
        const selectedVariantId =
          (this.section &&
            this.section.querySelector('input[name="id"]')?.value) ||
          this.getSelectedVariant();
        this.currentVariant = this.variantData.find(
          (variant) => variant.id === Number(selectedVariantId)
        );

        if (!this.enableVariantGroupImages || this.onlyImage) {
          setTimeout(() => {
            this.removeAttribute("data-media-loading");
          }, 100);
        }
        if (this.mediaLayout === "carousel") {
          setTimeout(() => {
            this.domNodes.thumbnails && this.domNodes.thumbnails.initSlider();
          });
          this.initGallerySlider();
        } else {
          const initSliders = () => {
            this.domNodes.mediaGallery && this.domNodes.mediaGallery.destroy();
            this.domNodes.thumbnails && this.domNodes.thumbnails.destroy();
            this.domNodes.mediaGallery &&
              this.domNodes.mediaGallery.initSlider();
            this.domNodes.thumbnails && this.domNodes.thumbnails.initSlider();
            this.mediaGallery = this.domNodes.mediaGallery.slider.instance;
            this.thumbnails =
              this.domNodes.thumbnails &&
              this.domNodes.thumbnails.slider.instance;
            // Remove Flickity's internal watchCSS resize listener to prevent it
            // from deactivating the slider during fullscreen-induced orientation change
            if (this.mediaGallery) window.removeEventListener("resize", this.mediaGallery);
            if (this.thumbnails) window.removeEventListener("resize", this.thumbnails);
            this.mediaGallery.on("change", this.onSlideChanged.bind(this));
            if (
              this.currentVariant &&
              this.currentVariant.featured_media &&
              !this.disableDefaultVariant &&
              !this.showFeaturedMedia
            ) {
              this.setActiveMedia(this.currentVariant.featured_media.id);
            }
          };
          if (FoxThemeSettings.isMobile) {
            initSliders();
          }
          document.addEventListener("matchMobile", initSliders);
          document.addEventListener("unmatchMobile", () => {
            if (document.fullscreenElement || document.webkitFullscreenElement) return;
            this.domNodes.mediaGallery && this.domNodes.mediaGallery.destroy();
            this.domNodes.thumbnails && this.domNodes.thumbnails.destroy();
            if (
              this.currentVariant &&
              this.currentVariant.featured_media &&
              !this.disableDefaultVariant &&
              !this.showFeaturedMedia
            ) {
              this.setActiveMedia(this.currentVariant.featured_media.id);
            }
          });
          const onFullscreenExit = () => {
            if (!document.fullscreenElement && !document.webkitFullscreenElement && FoxThemeSettings.isMobile) {
              initSliders();
            }
          };
          document.addEventListener("fullscreenchange", onFullscreenExit);
          document.addEventListener("webkitfullscreenchange", onFullscreenExit);
          if (this.enableZoom) this.initImageZoom();
        }
        if (this.onlyImage && this.enableZoom) this.initImageZoom();

        const prevBtn = this.querySelector('.f-product__media-mobile-nav-btn.prev');
        const nextBtn = this.querySelector('.f-product__media-mobile-nav-btn.next');
        if (prevBtn) {
          prevBtn.addEventListener('click', (e) => {
            e.preventDefault();
            if (this.mediaGallery) {
              this.mediaGallery.previous();
            }
          });
        }
        if (nextBtn) {
          nextBtn.addEventListener('click', (e) => {
            e.preventDefault();
            if (this.mediaGallery) {
              this.mediaGallery.next();
            }
          });
        }

        if (this.mediaLayout !== "carousel") {
          this.filterMediaBySwatch();
        }

        setTimeout(() => {
          this.dispatchEvent(
            new CustomEvent("product-media:loaded", {
              bubbles: true,
              detail: {
                currentVariant: this.currentVariant,
              },
            })
          );
        }, 100);
      }

      update() {
        if (this.domNodes.mediaGallery) this.domNodes.mediaGallery.update();
        if (this.domNodes.thumbnails) this.domNodes.thumbnails.update();
      }

      initGallerySlider() {
        if (!this.domNodes.medias.length) return;
        this.selectedSlide = null;
        this.selectedMediaType = "";
        this.check = setInterval(() => {
          this.mediaGallery =
            this.domNodes.mediaGallery.slider &&
            this.domNodes.mediaGallery.slider.instance;
          this.thumbnails =
            this.domNodes.thumbnails &&
            this.domNodes.thumbnails.slider &&
            this.domNodes.thumbnails.slider.instance;

          if (this.mediaGallery && typeof this.mediaGallery == "object") {
            clearInterval(this.check);
            this.mediaGallery.on("change", this.onSlideChanged.bind(this));

            if (this.thumbnails) {
              if (this.thumbnails.cells && this.thumbnails.cells[0]) {
                this.thumbnails.select(0);
                this.handleScreenChange();
              }
            }
            if (this.enableZoom) this.initImageZoom();
            this.filterMediaBySwatch();
            if (
              this.currentVariant &&
              this.currentVariant.featured_media &&
              !this.disableDefaultVariant &&
              !this.showFeaturedMedia
            ) {
              this.setActiveMedia(this.currentVariant.featured_media.id);
            }
          }
        }, 100);
      }

      getVariantData() {
        this.variantData =
          this.variantData ||
          JSON.parse(
            this.querySelector('[type="application/json"]').textContent
          );
        return this.variantData;
      }

      initImageZoom() {
        let dataSource = [];
        const allMedia = this.querySelectorAll(".f-product__media");
        if (allMedia) {
          allMedia.forEach((media) => {
            if (media.dataset.mediaType === "image") {
              dataSource.push({
                id: media.dataset.mediaIndex,
                src: media.dataset.src,
                width: media.dataset.pswpWidth,
                height: media.dataset.pswpHeight,
                mediaType: media.dataset.mediaType,
              });
            }
            if (media.dataset.mediaType === "model") {
              const html = `<div class="pswp__item--${media.dataset.mediaType
                }">${media.querySelector("product-model").outerHTML}</div>`;
              dataSource.push({
                id: media.dataset.mediaIndex,
                html: html,
                mediaType: media.dataset.mediaType,
              });
            }

            if (
              media.dataset.mediaType === "video" ||
              media.dataset.mediaType == "external_video"
            ) {
              const html = `<div class="pswp__item--${media.dataset.mediaType
                }">${media.querySelector("deferred-media").outerHTML}</div>`;
              dataSource.push({
                id: media.dataset.mediaIndex,
                html: html,
                mediaType: media.dataset.mediaType,
              });
            }
          });
        }

        const options = {
          dataSource: dataSource,
          pswpModule: window.FoxTheme.PhotoSwipe,
          bgOpacity: 1,
          arrowPrev: false,
          arrowNext: false,
          zoom: false,
          close: false,
          counter: false,
          preloader: false,
        };

        this.lightbox = new window.FoxTheme.PhotoSwipeLightbox(options);

        this.lightbox.addFilter("thumbEl", (thumbEl, data, index) => {
          const el = this.querySelector(
            '[data-media-index="' + data.id + '"] img'
          );
          if (el) {
            return el;
          }
          return thumbEl;
        });

        this.lightbox.addFilter("placeholderSrc", (placeholderSrc, slide) => {
          const el = this.querySelector(
            '[data-media-index="' + slide.data.id + '"] img'
          );
          if (el) {
            return el.src;
          }
          return placeholderSrc;
        });

        this.lightbox.on("change", () => {
          const currIndex = this.lightbox.pswp.currIndex;
          if (!this.onlyImage) {
            const slider =
              this.domNodes.mediaGallery.slider &&
              this.domNodes.mediaGallery.slider.instance;
            if (slider) {
              setTimeout(() => {
                slider.select(currIndex, false, true);
              }, 300);
            }
          }
        });

        this.lightbox.on("pointerDown", (e) => {
          const currSlide = this.lightbox.pswp.currSlide.data;
          if (
            currSlide.mediaType === "video" ||
            currSlide.mediaType === "model" ||
            currSlide.mediaType === "external_video"
          ) {
            e.preventDefault();
          }
        });

        this.lightbox.on("uiRegister", () => {
          if (!this.onlyImage) {
            this.lightbox.pswp.ui.registerElement({
              name: "next",
              ariaLabel: "Next slide",
              order: 3,
              isButton: true,
              html: '<svg class="pswp-icon-next f-rlt-reverse-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z" class="arrow" transform="translate(100, 100) rotate(180) "></path></svg>',
              onClick: (event, el) => {
                this.lightbox.pswp.next();
              },
            });
            this.lightbox.pswp.ui.registerElement({
              name: "prev",
              ariaLabel: "Previous slide",
              order: 1,
              isButton: true,
              html: '<svg class="pswp-icon-prev f-rlt-reverse-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z" class="arrow"></path></svg>',
              onClick: (event, el) => {
                this.lightbox.pswp.prev();
              },
            });
          }

          this.lightbox.pswp.ui.registerElement({
            name: "close-zoom",
            ariaLabel: "Close zoom image",
            order: 2,
            isButton: true,
            html: '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="f-icon-svg f-icon--medium  f-icon-close"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',
            onClick: (event, el) => {
              this.lightbox.pswp.close();
            },
          });
        });

        this.lightbox.init();

        if (this.zoomDelegateAdded) return;
        this.zoomDelegateAdded = true;

        addEventDelegate({
          selector: this.selectors.toggleZoom[0],
          context: this,
          handler: (e, media) => {
            const mediaContainer = media.closest(".f-product__media");
            const mediaType = mediaContainer
              ? mediaContainer.dataset.mediaType
              : media.dataset.mediaType;
            const mediaIndex =
              Number((mediaContainer || media).dataset.mediaIndex) || 0;

            if (FoxThemeSettings.isMobile) {
              const currentSlide = this.mediaGallery
                ? this.mediaGallery.selectedElement
                : null;
              const currentIndex = currentSlide
                ? Number(currentSlide.dataset.mediaIndex)
                : -1;

              if (mediaIndex !== currentIndex) {
                e.preventDefault();
                return false;
              }

              if (mediaType === "video" || mediaType === "external_video") {
                e.preventDefault();
                return false;
              }
            }

            const isImage = mediaType === "image";
            if (isImage) {
              this.lightbox.loadAndOpen(mediaIndex);
            }
          },
        });
      }

      onSlideChanged(index) {
        this.draggable = true;
        this.selectedSlide = this.mediaGallery.selectedElement;
        this.selectedMediaType = this.selectedSlide.dataset.mediaType;
        this.selectedMediaId = this.selectedSlide.dataset.mediaId;

        let mediaAspectRatio = this.selectedSlide.style.getPropertyValue('--aspect-ratio')?.trim();
        if (!mediaAspectRatio) {
          mediaAspectRatio = this.selectedSlide.dataset.aspectRatio;
        }
        this.domNodes.viewer.style.setProperty(
          "--media-aspect-ratio",
          mediaAspectRatio
        );

        if (
          "model,external_video,video".includes(this.selectedMediaType) &&
          this.draggable
        ) {
          this.toggleDraggable();
        } else {
          this.toggleDraggable(true);
        }

        if (this.selectedMediaType === "model") {
          this.domNodes.xrButton &&
            this.domNodes.xrButton.setAttribute(
              "data-shopify-model3d-id",
              this.selectedMediaId
            );
        }

        if (this.thumbnails) {
          this.thumbnails.select(index);
        }

        if (this.domNodes.sliderCounter) {
          this.domNodes.sliderCounter.textContent = index + 1;
        }

        const mobileCurrentCounter = this.querySelector('.f-product__media-mobile-nav-current');
        if (mobileCurrentCounter) {
          mobileCurrentCounter.textContent = index + 1;
        }

        this.domNodes.mediaGallery.classList.remove("pointer-move");

        this.playActiveMedia(this.selectedSlide);
      }

      handleScreenChange() {
        document.addEventListener("matchMobile", () => {
          this.domNodes.thumbnails.update();
          if (this.thumbnails.cells.length > 4) {
            this.thumbnails.options.wrapAround = true;
            this.domNodes.thumbnails.update();
          }
        });
        document.addEventListener("unmatchMobile", () => {
          this.domNodes.thumbnails.update();
          if (this.thumbnails.cells.length < 6) {
            this.thumbnails.options.wrapAround = false;
            this.domNodes.thumbnails.update();
          }
        });
        if (this.thumbnails.cells.length > 5) {
          this.domNodes.thumbnails.update();
          this.thumbnails.options.wrapAround = true;
          this.domNodes.thumbnails.update();
        }
      }

      toggleDraggable(status = false) {
        this.draggable = status;
        this.mediaGallery.options.draggable = status;
        this.mediaGallery.updateDraggable();
      }
      playActiveMedia(selected) {
        window.pauseAllMedia();
        const deferredMedia = selected.querySelector(".deferred-media");
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      setActiveMedia(mediaId, isInstant = true) {
        const selectedMedia = this.domNodes.mediaGallery.querySelector(
          `[data-media-id="${mediaId}"]`
        );
        if (!selectedMedia) return;
        const mediaIndex = Number(selectedMedia.dataset.mediaIndex);

        if (this.mediaLayout === "carousel" || FoxThemeSettings.isMobile) {
          this.mediaGallery &&
            this.mediaGallery.select(mediaIndex, false, isInstant);
        } else {
          this.scrollIntoView(selectedMedia);
        }
      }

      scrollIntoView(selectedMedia) {
        return

        if (FoxThemeSettings.isMobile) return;
        selectedMedia.scrollIntoView({
          behavior: "smooth",
        });
      }

      getSwatchValue() {
        const triggersStr = window.FoxThemeSettings?.color_swatches_trigger || "Color,Colour,Couleur,Farbe,Metal color,Hardwood Veneer";
        const triggerList = triggersStr.split(',').map(item => item.trim().toLowerCase());

        const variantPicker = this.section ? this.section.querySelector('variant-picker') : document.querySelector('variant-picker');
        if (variantPicker) {
          const fields = variantPicker.querySelectorAll('.variant-picker__field');
          for (const field of fields) {
            const optionName = field.dataset.optionName?.trim().toLowerCase();
            if (triggerList.includes(optionName)) {
              if (field.dataset.pickerField === 'select') {
                const select = field.querySelector('select');
                if (select) return select.value;
              } else {
                const checkedRadio = field.querySelector('input[type="radio"]:checked');
                if (checkedRadio) return checkedRadio.value;
              }
            }
          }
        }
        return null;
      }

      hasSwatchOption() {
        const triggersStr = window.FoxThemeSettings?.color_swatches_trigger || "Color,Colour,Couleur,Farbe,Metal color,Hardwood Veneer";
        const triggerList = triggersStr.split(',').map(item => item.trim().toLowerCase());

        const variantPicker = this.section ? this.section.querySelector('variant-picker') : document.querySelector('variant-picker');
        if (variantPicker) {
          const fields = variantPicker.querySelectorAll('.variant-picker__field');
          for (const field of fields) {
            const optionName = field.dataset.optionName?.trim().toLowerCase();
            if (triggerList.includes(optionName)) {
              return true;
            }
          }
        }
        return false;
      }

      shouldShowMedia(altText, activeSwatchValue) {
        if (!altText) return true;

        const altLower = altText.toLowerCase();
        if (!altLower.includes('swatch_')) {
          return true;
        }

        if (!activeSwatchValue) {
          return false;
        }

        const normalizedSwatch = activeSwatchValue.trim().toLowerCase();
        const targets = [
          'swatch_' + normalizedSwatch.replace(/[^a-z0-9]+/g, '_'),
          'swatch_' + normalizedSwatch.replace(/[^a-z0-9]+/g, '-'),
          'swatch_' + normalizedSwatch
        ];

        return targets.some(target => altLower.includes(target));
      }

      filterMediaBySwatch(isInstant = true) {
        if (this._filterTimeout) {
          clearTimeout(this._filterTimeout);
        }
        this._filterTimeout = setTimeout(() => {
          this._filterMediaBySwatchActual(isInstant);
        }, 10);
      }

      _filterMediaBySwatchActual(isInstant) {
        if (!this.hasSwatchOption()) {
          this.restoreAllMedias(isInstant);
          return;
        }

        const activeSwatchValue = this.getSwatchValue();
        const isCarousel = this.mediaLayout === 'carousel' || (typeof FoxThemeSettings !== 'undefined' && FoxThemeSettings.isMobile) || (window.FoxThemeSettings && window.FoxThemeSettings.isMobile);

        const normalizedValue = activeSwatchValue ? activeSwatchValue.trim().toLowerCase() : '';
        if (this._lastFilteredSwatch === normalizedValue && !isInstant) {
          return;
        }
        this._lastFilteredSwatch = normalizedValue;

        const sortMedias = (medias) => {
          if (!activeSwatchValue) {
            return medias.filter(item => {
              const alt = item.dataset.mediaAlt || item.querySelector('img')?.getAttribute('alt') || '';
              return this.shouldShowMedia(alt, activeSwatchValue);
            });
          }

          const normalizedSwatch = activeSwatchValue.trim().toLowerCase();
          const targets = [
            'swatch_' + normalizedSwatch.replace(/[^a-z0-9]+/g, '_'),
            'swatch_' + normalizedSwatch.replace(/[^a-z0-9]+/g, '-'),
            'swatch_' + normalizedSwatch
          ];

          const swatchItems = [];
          const commonItems = [];
          
          medias.forEach(item => {
            const alt = item.dataset.mediaAlt || item.querySelector('img')?.getAttribute('alt') || '';
            if (this.shouldShowMedia(alt, activeSwatchValue)) {
              const altLower = alt.toLowerCase();
              if (targets.some(target => altLower.includes(target))) {
                swatchItems.push(item);
              } else {
                commonItems.push(item);
              }
            }
          });

          // Always sort to bring swatch images to front for both mobile carousel and desktop grid
          return [...swatchItems, ...commonItems];
        };

        const filteredMedias = sortMedias(this.domNodes.medias);
        const filteredThumbs = this.domNodes.thumbnailItems ? sortMedias(this.domNodes.thumbnailItems) : [];

        // When sorted, the swatch image is always first
        const activeIndex = (isCarousel && activeSwatchValue) ? 0 : -1;

        this.applyFilteredMedias(filteredMedias, filteredThumbs, isInstant, activeIndex);
      }

      restoreAllMedias(isInstant) {
        this.applyFilteredMedias(this.domNodes.medias, this.domNodes.thumbnailItems || [], isInstant);
      }

      applyFilteredMedias(filteredMedias, filteredThumbs, isInstant = true, forcedActiveIndex = -1) {
        const isCarousel = this.mediaLayout === 'carousel' || (typeof FoxThemeSettings !== 'undefined' && FoxThemeSettings.isMobile) || (window.FoxThemeSettings && window.FoxThemeSettings.isMobile);

        const mainMedia = this.querySelector(".f-product__media-list");
        const thumbMedia = this.querySelector(".f-product__media-thumbnails");

        // Determine if visible items actually changed to avoid layout & image reloading
        const currentVisibleMedias = isCarousel
          ? this.domNodes.medias.filter(item => !item.classList.contains('hidden'))
          : (mainMedia ? Array.from(mainMedia.children) : []);
        const currentVisibleThumbs = isCarousel
          ? (this.domNodes.thumbnailItems ? this.domNodes.thumbnailItems.filter(item => !item.classList.contains('hidden')) : [])
          : (thumbMedia ? Array.from(thumbMedia.children) : []);

        const mediasChanged = currentVisibleMedias.length !== filteredMedias.length ||
          !currentVisibleMedias.every((item, idx) => item === filteredMedias[idx]);
        const thumbsChanged = currentVisibleThumbs.length !== filteredThumbs.length ||
          !currentVisibleThumbs.every((item, idx) => item === filteredThumbs[idx]);

        const hasChanges = mediasChanged || thumbsChanged;

        if (hasChanges) {
          if (isCarousel) {
            // 1. Iterate through all medias and physically reorder the DOM
            let visibleMediaCount = 0;
            
            filteredMedias.forEach((item) => {
              if (item.parentNode) item.parentNode.appendChild(item); // Append to flickity-slider instead of mainMedia
              item.classList.add('f-product__media');
              item.classList.remove('hidden');
              item.setAttribute('data-media-index', visibleMediaCount);
              item.setAttribute('data-index', visibleMediaCount);
              const zoomButton = item.querySelector(".js-photoswipe--zoom");
              if (zoomButton) {
                zoomButton.setAttribute("data-media-index", visibleMediaCount);
              }
              visibleMediaCount++;
            });
            
            this.domNodes.medias.forEach((item) => {
              if (!filteredMedias.includes(item)) {
                if (item.parentNode) item.parentNode.appendChild(item);
                item.classList.remove('f-product__media');
                item.classList.add('hidden');
                item.removeAttribute('data-media-index');
                item.removeAttribute('data-index');
                const zoomButton = item.querySelector(".js-photoswipe--zoom");
                if (zoomButton) {
                  zoomButton.removeAttribute("data-media-index");
                }
              }
            });

            // 2. Iterate through all thumbnailItems and physically reorder the DOM
            let visibleThumbCount = 0;
            if (this.domNodes.thumbnailItems) {
              filteredThumbs.forEach((item) => {
                if (item.parentNode) item.parentNode.appendChild(item);
                item.classList.add('f-product__media-thumbnails-item');
                item.classList.remove('hidden');
                item.setAttribute('data-thumbnail-index', visibleThumbCount);
                item.setAttribute('data-index', visibleThumbCount);
                visibleThumbCount++;
              });
              
              this.domNodes.thumbnailItems.forEach((item) => {
                if (!filteredThumbs.includes(item)) {
                  if (item.parentNode) item.parentNode.appendChild(item);
                  item.classList.remove('f-product__media-thumbnails-item');
                  item.classList.add('hidden');
                  item.removeAttribute('data-thumbnail-index');
                  item.removeAttribute('data-index');
                }
              });
            }

            // 3. Update flickity-counter--total
            const totalCounter = this.querySelector('.flickity-counter--total');
            if (totalCounter) {
              totalCounter.textContent = visibleMediaCount;
            }

            // 4. Update page counter visibility
            const pageCounter = this.querySelector('.flickity-page-counter');
            if (pageCounter) {
              if (visibleMediaCount <= 1) {
                pageCounter.classList.add('hidden');
              } else {
                pageCounter.classList.remove('hidden');
              }
            }

            const mobileTotalCounter = this.querySelector('.f-product__media-mobile-nav-total');
            if (mobileTotalCounter) {
              mobileTotalCounter.textContent = visibleMediaCount;
            }

            const mobileNav = this.querySelector('.f-product__media-mobile-nav');
            if (mobileNav) {
              if (visibleMediaCount <= 1) {
                mobileNav.classList.add('hidden');
              } else {
                mobileNav.classList.remove('hidden');
              }
            }

            // 5. Update transitions class on thumbnail list container
            if (thumbMedia) {
              thumbMedia.classList.remove("disable-transition", "md:disable-transition");
              if (visibleThumbCount < 5) {
                thumbMedia.classList.add("disable-transition");
              }
              if (visibleThumbCount < 6) {
                thumbMedia.classList.add("md:disable-transition");
              }
            }

            // 6. Toggle visibility of thumbnails wrapper
            if (this.domNodes.thumbnailsWrapper) {
              if (visibleThumbCount <= 1) {
                this.domNodes.thumbnailsWrapper.style.display = 'none';
              } else {
                this.domNodes.thumbnailsWrapper.style.display = '';
              }
            }

            // 7. Reload cells and reposition for Flickity if initialized
            if (this.mediaGallery) {
              this.mediaGallery.options.wrapAround = visibleMediaCount > 1;
              this.mediaGallery.reloadCells();
              this.mediaGallery.reposition();
            }
            if (this.thumbnails) {
              this.thumbnails.options.wrapAround = visibleThumbCount > 5;
              this.thumbnails.reloadCells();
              this.thumbnails.reposition();
            }
          } else {
            // Desktop Non-Carousel (stacked/grid) Layout: Use original DOM replacement approach
            if (mainMedia) {
              mainMedia.innerHTML = "";
              filteredMedias.forEach((item, index) => {
                item.classList.add('f-product__media');
                item.classList.remove('hidden');
                const zoomButton = item.querySelector(".js-photoswipe--zoom");
                if (zoomButton) {
                  zoomButton.setAttribute("data-media-index", index);
                }
                item.setAttribute("data-media-index", index);
                item.setAttribute("data-index", index);
                mainMedia.appendChild(item);
              });
            }
            if (thumbMedia && filteredThumbs.length > 0) {
              thumbMedia.innerHTML = "";
              filteredThumbs.forEach((item, index) => {
                item.classList.add('f-product__media-thumbnails-item');
                item.classList.remove('hidden');
                item.setAttribute("data-thumbnail-index", index);
                item.setAttribute("data-index", index);
                thumbMedia.appendChild(item);
              });
            }
          }

          // 8. Update lightbox zoom datasource if enabled
          if (this.enableZoom) {
            if (this.lightbox) {
              this.lightbox.destroy();
            }
            this.initImageZoom();
          }

          // 9. Trigger window resize event to allow layout systems to adapt
          setTimeout(() => {
            window.dispatchEvent(new Event("resize"));
          }, 100);
        }

        // 10. Always select the correct slide index (still run this even if layout elements didn't change)
        if (isCarousel && this.mediaGallery) {
          let activeIndex = 0;

          if (forcedActiveIndex !== -1) {
            activeIndex = forcedActiveIndex;
          } else if (this.currentVariant && this.currentVariant.featured_media) {
            const featuredMediaId = String(this.currentVariant.featured_media.id);
            const foundIndex = filteredMedias.findIndex(item => String(item.dataset.mediaId) === featuredMediaId);
            if (foundIndex !== -1) {
              activeIndex = foundIndex;
            }
          }

          if (this.mediaGallery.cells && this.mediaGallery.cells.length > 0) {
            this.mediaGallery.select(activeIndex, false, true);
          }
          if (this.thumbnails && this.thumbnails.cells && this.thumbnails.cells.length > 0) {
            this.thumbnails.select(activeIndex, false, true);
          }

          const mobileCurrentCounter = this.querySelector('.f-product__media-mobile-nav-current');
          if (mobileCurrentCounter) {
            mobileCurrentCounter.textContent = activeIndex + 1;
          }
        }
      }
    }
  );
}
